import React, {useLayoutEffect, useRef, useState} from 'react';
import PropTypes from "prop-types";
import {createPortal} from 'react-dom';
import DateTimePicker from "./DateTimePicker";
import {dateToString} from "../../utils/DateUtils";

// Range companion to FloatingDateTimePicker. Two trigger fields (Start/End), but clicking either
// one opens the same floating panel: a keyword column plus Start and End calendars side by side.
// A keyword always covers the whole range - it can't be attached to just one side. The panel is
// portalled into document.body and centered under the trigger pair.
const FloatingDateRangePicker = ({
    startLabel, endLabel, keywordsLabel, type, keywordOptions, value, onChange, disabled, className,
    startPlaceholder, endPlaceholder
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState(null);
    const [calendarHeight, setCalendarHeight] = useState(null);
    // Tracks what's been picked during this open, not what the incoming value already had -
    // editing one bound of a complete range shouldn't auto-close just because the other,
    // untouched bound still has an old value.
    const [touched, setTouched] = useState({start: false, end: false});
    const containerRef = useRef(null);
    const panelRef = useRef(null);
    const startSectionRef = useRef(null);
    const endSectionRef = useRef(null);

    // Centers the panel on the trigger pair once its actual (wider) width is known; until then,
    // falls back to the trigger's own left edge so there's something reasonable to render before
    // the first measurement.
    const computePosition = () => {
        if (!containerRef.current) {
            return null;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const top = rect.bottom + 4;
        if (panelRef.current) {
            const panelWidth = panelRef.current.offsetWidth;
            const left = Math.max(8, Math.min(centerX - panelWidth / 2, window.innerWidth - panelWidth - 8));
            return {top, left, centerX, ready: true};
        }
        return {top, left: rect.left, centerX, ready: false};
    };

    const open = () => {
        // Both trigger fields call this on every click/focus - if the panel is already open,
        // re-clicking a trigger must be a no-op, not a fresh open, or it would reset `touched` and
        // silently discard whichever side the user already completed.
        if (disabled || isOpen) {
            return;
        }
        setPosition(computePosition());
        setCalendarHeight(null);
        setTouched({start: false, end: false});
        setIsOpen(true);
    };

    useLayoutEffect(() => {
        if (!isOpen || !panelRef.current || (position && position.ready)) {
            return;
        }
        setPosition(computePosition());
    });

    // CSS can't cap the keyword column to "whichever calendar is taller" on its own, so measure
    // both calendars once rendered and use the max as the keyword column's height cap.
    useLayoutEffect(() => {
        if (!isOpen || !startSectionRef.current || !endSectionRef.current || calendarHeight) {
            return;
        }
        setCalendarHeight(Math.max(startSectionRef.current.offsetHeight, endSectionRef.current.offsetHeight));
    });

    useLayoutEffect(() => {
        if (!isOpen) {
            return;
        }
        const handleOutsideClick = (event) => {
            // Uses composedPath(), not .contains(event.target) - a click inside the panel can
            // trigger a synchronous re-render (e.g. advancing day -> hour -> minute swaps out the
            // whole grid, since items are keyed by index and each level has different content) that
            // detaches the clicked node from the DOM before this same event finishes bubbling here.
            // A detached node is never "contained" by anything, so contains() would wrongly read as
            // an outside click; composedPath() is a snapshot of the event's path at dispatch time,
            // unaffected by DOM changes made while the event is still bubbling.
            const path = event.composedPath ? event.composedPath() : [event.target];
            const clickedTrigger = containerRef.current && path.includes(containerRef.current);
            const clickedPanel = panelRef.current && path.includes(panelRef.current);
            if (!clickedTrigger && !clickedPanel) {
                setIsOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };
        const handleReposition = () => setPosition(computePosition());

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        window.addEventListener("scroll", handleReposition, true);
        window.addEventListener("resize", handleReposition);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
            window.removeEventListener("scroll", handleReposition, true);
            window.removeEventListener("resize", handleReposition);
        };
    }, [isOpen]);

    const selectKeyword = (code) => {
        onChange && onChange({keyword: code});
        setIsOpen(false);
    };

    // onChange just updates the value as the user drills day -> hour -> minute; it doesn't decide
    // when a field is "done". completeStart/completeEnd (from DateTimePicker's onComplete, which
    // only fires on the final level) mark a field as touched, and the panel closes once both
    // start and end are touched.
    const selectStart = (start) => onChange && onChange({...value, keyword: undefined, start});
    const selectEnd = (end) => onChange && onChange({...value, keyword: undefined, end});

    const completeStart = () => {
        setTouched(prev => {
            const next = {...prev, start: true};
            if (next.start && next.end) {
                setIsOpen(false);
            }
            return next;
        });
    };
    const completeEnd = () => {
        setTouched(prev => {
            const next = {...prev, end: true};
            if (next.start && next.end) {
                setIsOpen(false);
            }
            return next;
        });
    };

    const displayFor = (boundValue) => boundValue ? dateToString(boundValue, type !== "datetime", true) : "";

    const keywordDisplay = () => {
        if (!value?.keyword) {
            return null;
        }
        const option = (keywordOptions || []).find(opt => opt.code === value.keyword);
        return option ? option.label : value.keyword;
    };

    return (
        <div ref={containerRef} className={"floating-date-range-trigger " + (className || "")}>
            <label className={"field has-float-label dropdown date-time" + (startPlaceholder ? " has-default-placeholder" : "")}>
                <input type="text" readOnly value={keywordDisplay() || displayFor(value?.start)} disabled={disabled}
                       placeholder={startPlaceholder || " "} className="input" onClick={open} onFocus={open}/>
                <span>{startLabel}</span>
                <div className="dropdown-indicator right-decoration fz fz-calendar-alt solid"/>
            </label>
            <label className={"field has-float-label dropdown date-time" + (endPlaceholder ? " has-default-placeholder" : "")}>
                <input type="text" readOnly value={keywordDisplay() || displayFor(value?.end)} disabled={disabled}
                       placeholder={endPlaceholder || " "} className="input" onClick={open} onFocus={open}/>
                <span>{endLabel}</span>
                <div className="dropdown-indicator right-decoration fz fz-calendar-alt solid"/>
            </label>
            {isOpen && position && createPortal(
                <div ref={panelRef}
                     className="floating-portal-panel has-keywords"
                     style={{position: "fixed", top: position.top, left: position.left, visibility: position.ready ? "visible" : "hidden"}}>
                    <div className="picker-section">
                        <div className="section-label">{keywordsLabel}</div>
                        <div className="keyword-panel" style={{maxHeight: calendarHeight || undefined}}>
                            {(keywordOptions || []).map(option => (
                                <div key={option.code}
                                     className={"keyword-chip" + (value?.keyword === option.code ? " active" : "")}
                                     onClick={() => selectKeyword(option.code)}>
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="picker-section" ref={startSectionRef}>
                        <div className="section-label">{startLabel}</div>
                        <DateTimePicker type={type} value={value?.keyword ? null : value?.start} onChange={selectStart}
                                        onComplete={completeStart}/>
                    </div>
                    <div className="picker-section" ref={endSectionRef}>
                        <div className="section-label">{endLabel}</div>
                        <DateTimePicker type={type} value={value?.keyword ? null : value?.end} onChange={selectEnd}
                                        onComplete={completeEnd}/>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

FloatingDateRangePicker.propTypes = {
    startLabel: PropTypes.string,
    endLabel: PropTypes.string,
    keywordsLabel: PropTypes.string,
    type: PropTypes.oneOf(["date", "datetime"]),
    keywordOptions: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    })),
    value: PropTypes.shape({
        keyword: PropTypes.string,
        start: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
        end: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
    }),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    // Shown as native grayed-out placeholder text when each respective field is empty - the
    // relevant default value for that bound (e.g. "31.08.2026").
    startPlaceholder: PropTypes.string,
    endPlaceholder: PropTypes.string,
};

FloatingDateRangePicker.defaultProps = {
    type: "date",
    keywordOptions: [],
    keywordsLabel: "Keywords",
    disabled: false,
};

export default FloatingDateRangePicker;
